import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import ButtonMerge from '../../../components/_button/ButtonMerge';
import ButtonPublish from '../../../components/_button/ButtonPublish';
import DialogConfirm from '../../../components/_dialog/DialogConfirm';
import SelectYesNo from '../../../components/_select/SelectYesNo';
import {
  ICompanyKeywordClassificationQueryData,
  ICompanyKeywordClassificationVars,
  useCompanyKeywordClassificationQuery,
} from '../../../../graphql/query/companyKeywordClassification/useCompanyKeywordClassificationQuery';
import ButtonDelete from '../../../components/_button/ButtonDelete';
import { useDeleteCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/deleteCompanyKeywordClassificationsMutation';
import useNotification from '../../../components/_hook/useNotification';
import ButtonUnpublish from '../../../components/_button/ButtonUnpublish';
import GraphQLTable from '../../../components/_table/GraphQLTable';
import { useUnpublishCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/unpublishCompanyKeywordClassificationsMutation';
import ButtonCreate from '../../../components/_button/ButtonCreate';
import { usePublishCompanyKeywordClassificationsMutation } from '../../../../graphql/mutation/companyKeywordClassification/publishCompanyKeywordClassificationsMutation';
import FormCompanyKeywordCreateClassification from './_module/FormCompanyKeywordCreateClassification';
import FormCompanyKeywordMergeClassification from './_module/FormCompanyKeywordMergeClassification';
import BusinessClassificationTableItem from './BusinessClassificationTableItem';
import Helmet from '../../../components/Helmet';
import { ICompanyKeywordClassificationSchema } from '../../../../graphql/type/companyKeywordClassification';
import BusinessLayout from '../../../layout/Business';
import Context from '../../../context';

function BusinessClassificationTable() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const [tableRefreshKey, setTableRefreshKey] = useState<number>(0);
  const { company } = useContext(Context);
  const [selectedItems, setSelectedItems] = useState<
    ICompanyKeywordClassificationSchema[]
  >([]);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenMergeDialog, setIsOpenMergeDialog] = useState(false);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [
    deleteCompanyKeywordClassificationsMutation,
  ] = useDeleteCompanyKeywordClassificationsMutation(
    `
    isSuccess
  `,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', {
            item: `${selectedItems.length} ${t('classification', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
        setTableRefreshKey(new Date().getTime());
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    publishCompanyKeywordClassificationsMutation,
  ] = usePublishCompanyKeywordClassificationsMutation(
    `updatedCompanyKeywordClassifications {
				_id
				id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('classification', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishCompanyKeywordClassificationsMutation,
  ] = useUnpublishCompanyKeywordClassificationsMutation(
    `updatedCompanyKeywordClassifications {
				_id
				id
				status
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___updated', {
            item: `${selectedItems.length} ${t('classification', {
              count: selectedItems.length,
            })}`,
          })
        );
        setSelectedItems([]);
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  function toggleDeleteDialog() {
    setIsOpenDeleteDialog(!isOpenDeleteDialog);
  }

  async function onClickPublish() {
    await publishCompanyKeywordClassificationsMutation({
      variables: {
        companyId: company._id,
        classificationIds: selectedItems.map((item) => item.id),
      },
    });
  }

  async function onClickUnpublish() {
    await unpublishCompanyKeywordClassificationsMutation({
      variables: {
        companyId: company._id,
        classificationIds: selectedItems.map((item) => item.id),
      },
    });
  }

  async function onClickDelete() {
    await deleteCompanyKeywordClassificationsMutation({
      variables: {
        companyId: company._id,
        classificationIds: selectedItems.map((item) => item.id),
      },
    });
  }

  return (
    <>
      <Helmet
        title={`${t('classification', { count: 2 })} | ${t('business')}`}
      />
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete')}
        open={isOpenDeleteDialog}
        onClose={toggleDeleteDialog}
        onConfirm={onClickDelete}
      />
      <Dialog
        open={isOpenCreateDialog}
        onClose={() => setIsOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCompanyKeywordCreateClassification
          companyId={company._id}
          onSubmit={() => {
            setIsOpenCreateDialog(false);
            setTableRefreshKey(new Date().getTime());
          }}
        />
      </Dialog>
      <Dialog
        open={isOpenMergeDialog}
        onClose={() => setIsOpenMergeDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCompanyKeywordMergeClassification
          companyId={company._id}
          companyClassificationIds={selectedItems.map((item) => item._id)}
          onSubmit={() => {
            setIsOpenMergeDialog(false);
            setTableRefreshKey(new Date().getTime());
          }}
        />
      </Dialog>
      <ButtonCreate
        variant="contained"
        color="primary"
        text={t('new')}
        onClick={() => setIsOpenCreateDialog(true)}
      />
      <GraphQLTable<
        ICompanyKeywordClassificationQueryData,
        ICompanyKeywordClassificationSchema,
        ICompanyKeywordClassificationVars
      >
        refreshKey={tableRefreshKey}
        select={`
        	_id
        	id
        	company
        	status
			classificationData {
			  _id
			  name
              slug
              la
              status
			}
        `}
        useQuery={useCompanyKeywordClassificationQuery}
        renderHeader={[
          {
            field: '_id',
            label: t('id'),
            align: 'left',
            sortable: true,
          },
          {
            field: 'name',
            label: t('name'),
            align: 'left',
          },
          {
            field: 'la',
            label: t('language'),
            align: 'center',
          },
          {
            field: 'action',
            label: t('action'),
            align: 'right',
          },
        ]}
        renderItem={(item) => (
          <BusinessClassificationTableItem
            item={item}
            setTableRefreshKey={setTableRefreshKey}
          />
        )}
        useCheckbox
        onItemSelected={setSelectedItems}
        selectedItems={selectedItems}
        searchFields={{
          _id: { label: t('id') },
          name: { label: t('name') },
        }}
        filterFields={{
          status: {
            label: t('status'),
            component: SelectYesNo,
            componentProps: {
              menuPosition: 'fixed',
              isClearable: true,
            },
            onChange: (option) => {
              if (option) {
                if (option.value === true) {
                  return true;
                }
                if (option.value === false) {
                  return false;
                }
              }

              return null;
            },
          },
        }}
        title={t('classification', { count: 2 })}
        defaultFilterOptions={{
          companyId: company._id,
        }}
        defaultSort="createdTime"
        defaultSortOrder={-1}
        actionButtons={[
          <ButtonMerge
            key="merge"
            variant="contained"
            color="primary"
            size="small"
            text={t('merge')}
            onClick={() => {
              if (selectedItems.length < 2) {
                notification.warning(
                  t('please_select_at_least__count____name__', {
                    count: 2,
                    name: t('classification').toLowerCase(),
                  })
                );
              } else {
                setIsOpenMergeDialog(true);
              }
            }}
          />,
          <ButtonPublish
            key="publish"
            variant="contained"
            color="primary"
            size="small"
            text={t('publish')}
            onClick={onClickPublish}
          />,
          <ButtonUnpublish
            key="unpublish"
            color="primary"
            variant="contained"
            size="small"
            text={t('unpublish')}
            onClick={onClickUnpublish}
          />,
          <ButtonDelete
            key="delete"
            variant="contained"
            size="small"
            text={t('delete')}
            onClick={toggleDeleteDialog}
          />,
        ]}
        itemKey="_id"
      />
    </>
  );
}

export default () => <BusinessLayout main={<BusinessClassificationTable />} />;
